import { Injectable, inject } from '@angular/core';
import { Firestore, collection, getDocs, doc, getDoc, query, orderBy, limit, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import {
  isValidVersion,
  sortCatalogVersionsDescending,
  VersionCatalogEntry
} from '../utils/coinsing-version.util';
import { DeleteReleaseResult } from '../utils/coinsing-delete-release.util';

export interface CoinSingDeviceResult {
  deviceId: string;
  result: 'accepted' | 'skipped' | 'failed';
  reason?: string;
  fromVersion?: string | null;
  isRollback?: boolean;
  alreadyInstalled?: boolean;
}

export interface CoinSingReleaseOutcome {
  batchId: string;
  releaseId: string;
  version: string;
  force: boolean;
  scope: 'devices' | 'all';
  results: CoinSingDeviceResult[];
  counts: {
    accepted: number;
    skipped: number;
    failed: number;
    rollbacks: number;
  };
  notListedSkipped?: number;
  auditWritten: boolean;
}

export interface CoinSingReleaseAuditItem {
  id: string;
  action: 'release' | 'release_all' | 'note_edit' | 'release_delete' | 'device_status_change' | 'publish';
  timestamp?: any;
  createdAt?: any;
  version?: string;
  actorEmail?: string;
  actorUid?: string;
  deviceIds?: string[];
  results?: CoinSingDeviceResult[];
  counts?: { accepted: number; skipped: number; failed: number; rollbacks: number };
  force?: boolean;
  restart?: boolean;
  noteSnapshot?: string;
  previousVersion?: string | null;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class CoinsingReleaseService {
  private firestore = inject(Firestore);
  private functions = inject(Functions);
  private auth = inject(Auth);

  /**
   * Fetches all available CoinSing releases from the `coinsing_releases` collection.
   * Sorts versions descending by semver; invalid versions are placed at the very end.
   */
  async getAppReleases(): Promise<VersionCatalogEntry[]> {
    try {
      const releasesRef = collection(this.firestore, 'coinsing_releases');
      const snap = await getDocs(releasesRef);
      const entries: VersionCatalogEntry[] = snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          version: data['version'] || d.id,
          isValid: isValidVersion(data['version'] || d.id),
          environment: data['environment'] || 'production',
          publishedAt: data['publishedAt'],
          publishedBy: data['publishedBy'] || 'Unknown',
          releaseNote: data['releaseNote'] || '',
          releaseNoteUpdatedAt: data['releaseNoteUpdatedAt'],
          releaseNoteUpdatedBy: data['releaseNoteUpdatedBy'],
          sha256: data['sha256'],
          sizeBytes: data['sizeBytes'],
          fileCount: data['fileCount'],
          buildConfiguration: data['buildConfiguration'],
          gitCommit: data['gitCommit'],
          storagePath: data['storagePath'],
          manifestPath: data['manifestPath'],
          deleteState: data['deleteState']
        };
      });

      return sortCatalogVersionsDescending(entries);
    } catch (err) {
      console.error('Error fetching CoinSing releases:', err);
      throw err;
    }
  }

  /**
   * Fetches specific release version details and its formatted release note.
   */
  async getAppReleaseVersion(version: string): Promise<VersionCatalogEntry | null> {
    try {
      const docRef = doc(this.firestore, 'coinsing_releases', version);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      const data = snap.data();
      return {
        id: snap.id,
        version: data['version'] || snap.id,
        isValid: isValidVersion(data['version'] || snap.id),
        publishedAt: data['publishedAt'],
        publishedBy: data['publishedBy'],
        releaseNote: data['releaseNote'] || '',
        releaseNoteUpdatedAt: data['releaseNoteUpdatedAt'],
        releaseNoteUpdatedBy: data['releaseNoteUpdatedBy'],
        ...data
      };
    } catch (err) {
      console.error(`Error fetching release version ${version}:`, err);
      throw err;
    }
  }

  /**
   * Updates release notes on a published CoinSing version via Cloud Function callable.
   * Enforces server-side sanitization and permissions (`konobox-management_release-device`).
   */
  async saveAppReleaseNote(version: string, releaseNote: string): Promise<any> {
    const editFn = httpsCallable<{ version: string; releaseNote: string }, any>(
      this.functions,
      'coinsingEditReleaseNote'
    );
    const res = await editFn({ version, releaseNote });
    return res.data;
  }

  /**
   * KAN-578: permanently deletes a released version (Storage package + manifest + catalog doc) via the
   * `coinsingDeleteRelease` callable. Rejections carry `details.reason`; see mapDeleteError.
   */
  async deleteRelease(version: string, confirmVersion: string): Promise<DeleteReleaseResult> {
    const deleteFn = httpsCallable<{ version: string; confirmVersion: string }, DeleteReleaseResult>(
      this.functions,
      'coinsingDeleteRelease'
    );
    const res = await deleteFn({ version, confirmVersion });
    return res.data;
  }

  /**
   * Targets a CoinSing version to a single device via Cloud Function callable.
   */
  async releaseAppToDevice(
    deviceId: string,
    version: string,
    force: boolean = false,
    rollbackConfirmed: boolean = false
  ): Promise<CoinSingReleaseOutcome> {
    return this.releaseAppToSelectedDevices([deviceId], version, force, rollbackConfirmed);
  }

  /**
   * Targets a CoinSing version to multiple selected devices via Cloud Function callable.
   */
  async releaseAppToSelectedDevices(
    deviceIds: string[],
    version: string,
    force: boolean = false,
    rollbackConfirmed: boolean = false
  ): Promise<CoinSingReleaseOutcome> {
    const releaseFn = httpsCallable<any, CoinSingReleaseOutcome>(
      this.functions,
      'coinsingReleaseApp'
    );
    const res = await releaseFn({
      version,
      deviceIds,
      force,
      rollbackConfirmed
    });
    return res.data;
  }

  /**
   * Releases a CoinSing version to ALL active Konobox devices via Cloud Function callable.
   * Requires `konobox-management_release-all` and `konobox-management_release-device`.
   */
  async releaseAppToAllDevices(
    version: string,
    includeTestMachines: boolean = false,
    force: boolean = false,
    rollbackConfirmed: boolean = false
  ): Promise<CoinSingReleaseOutcome> {
    const releaseFn = httpsCallable<any, CoinSingReleaseOutcome>(
      this.functions,
      'coinsingReleaseApp'
    );
    const res = await releaseFn({
      version,
      scope: 'all',
      includeTestMachines,
      force,
      rollbackConfirmed
    });
    return res.data;
  }

  /**
   * Queries release audit history from `coinsing_release_audit`.
   * Returns append-only history records ordered by timestamp descending.
   */
  async getReleaseHistory(deviceId?: string, limitCount: number = 25): Promise<CoinSingReleaseAuditItem[]> {
    try {
      const auditRef = collection(this.firestore, 'coinsing_release_audit');
      let q = query(auditRef, orderBy('timestamp', 'desc'), limit(limitCount));
      const snap = await getDocs(q);
      const items: CoinSingReleaseAuditItem[] = [];

      snap.docs.forEach(d => {
        const data = d.data();
        const item: CoinSingReleaseAuditItem = {
          id: d.id,
          action: data['action'],
          timestamp: data['timestamp'] || data['createdAt'],
          version: data['version'],
          actorEmail: data['actorEmail'] || data['publishedBy'] || 'Unknown',
          actorUid: data['actorUid'],
          deviceIds: data['deviceIds'] || [],
          results: data['results'] || [],
          counts: data['counts'] || { accepted: 0, skipped: 0, failed: 0, rollbacks: 0 },
          force: data['force'] === true,
          restart: data['restart'] === true,
          noteSnapshot: data['noteSnapshot'] || '',
          previousVersion: data['previousVersion'],
          ...data
        };

        if (!deviceId || (item.deviceIds && item.deviceIds.includes(deviceId))) {
          items.push(item);
        }
      });

      return items;
    } catch (err) {
      console.error('Error fetching release history:', err);
      return [];
    }
  }

  /**
   * KAN-434 AC-1: Queues a remote command (RESTART_APP, RESTART_PC, UPLOAD_LOGS) for a CoinSing kiosk.
   * Calls callable `coinsingDispatchCommand`.
   */
  async dispatchCommand(
    deviceId: string,
    command: 'RESTART_APP' | 'RESTART_PC' | 'UPLOAD_LOGS',
    logDate?: string
  ): Promise<{ commandId: string; status: string; command: string; deviceId: string }> {
    const dispatchFn = httpsCallable<any, { commandId: string; status: string; command: string; deviceId: string }>(
      this.functions,
      'coinsingDispatchCommand'
    );
    const res = await dispatchFn({
      deviceId,
      command,
      logDate: logDate || undefined
    });
    return res.data;
  }
}

